import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { api } from '../lib/api.js';
import { INDUSTRY_LABEL } from '../lib/industries.js';
import { SITE_TYPES, SITE_STATUSES, typeStyle } from '../lib/siteTypes.js';
import SiteForm from '../components/SiteForm.jsx';
import SupplyChainImport from './SupplyChainImport.jsx';
import UploadsList from '../components/UploadsList.jsx';

const SEV_TEXT = { 1: 'Advisory', 2: 'Warning', 3: 'Critical' };

/**
 * Sites page (the account menu's "Sites", expanded): every site the user's
 * teams own — the same records as the map pins — with search, filters,
 * monitoring status, active alerts and the routes that use each site.
 */
export default function SitesPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [sites, setSites] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);

  const teams = useMemo(() => auth.teams ?? [], [auth.teams]);
  const teamFilter = params.get('team') ?? 'all';
  const typeFilter = params.get('type') ?? 'all';
  const statusFilter = params.get('status') ?? 'all';
  const q = params.get('q') ?? '';
  const uploading = params.get('upload') === '1';
  const importTeams = teams.filter((t)=>['owner','admin'].includes(t.role));
  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === 'all') next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (!teams.length) return setSites([]);
    try {
      const [s, r, a] = await Promise.all([
        Promise.all(teams.map((t) => api(`/teams/${t.id}/sites`))),
        Promise.all(teams.map((t) => api(`/teams/${t.id}/routes`).catch(() => []))),
        api('/alerts').catch(() => []),
      ]);
      setSites(s.flat());
      setRoutes(r.flat());
      setAlerts(a);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
    return undefined;
  }, [teams]);
  useEffect(() => {
    load();
  }, [load]);

  const alertsBySite = useMemo(() => {
    const m = new Map();
    for (const a of alerts) {
      if (!a.siteId || a.status !== 'active') continue;
      const cur = m.get(a.siteId) ?? { count: 0, worst: 0 };
      m.set(a.siteId, { count: cur.count + 1, worst: Math.max(cur.worst, a.severityRank ?? 0) });
    }
    return m;
  }, [alerts]);
  const routesBySite = useMemo(() => {
    const m = new Map();
    for (const r of routes) for (const st of r.stops ?? []) if (st.siteId) m.set(st.siteId, [...(m.get(st.siteId) ?? []), r]);
    return m;
  }, [routes]);

  const shown = useMemo(() => {
    const text = q.trim().toLowerCase();
    return (sites ?? [])
      .filter(
        (s) =>
          (teamFilter === 'all' || s.teamId === teamFilter) &&
          (typeFilter === 'all' || s.type === typeFilter) &&
          (statusFilter === 'all' || s.status === statusFilter) &&
          (!text || [s.name, s.address, s.organization, s.municipality, s.countyName, s.state].some((x) => x?.toLowerCase().includes(text))),
      )
      .sort((a, b) => (alertsBySite.get(b.id)?.worst ?? 0) - (alertsBySite.get(a.id)?.worst ?? 0) || a.name.localeCompare(b.name));
  }, [sites, teamFilter, typeFilter, statusFilter, q, alertsBySite]);

  if (auth.loading) return <div className="app"><div className="loading">Loading…</div></div>;
  if (!auth.user) return <Navigate to="/login" replace />;

  const addTeam = teams.find((t) => t.id === teamFilter) ?? teams[0];

  return (
    <div className="app">
      <div className="page-head">
        <Link to="/" className="btn-link">
          ← Map
        </Link>
      </div>
      <header className="masthead">
        <div>
          <h1>Sites</h1>
          <p className="sub">
            Every saved site and map pin for your teams. Covered sites are checked against official alerts every few minutes
            and against weather thresholds and work rules every few hours.
          </p>
        </div>
        <div className="masthead-actions">
          {importTeams.length > 0 && <Link className="btn primary" to={`/sites?upload=1${teamFilter !== 'all' ? `&team=${encodeURIComponent(teamFilter)}` : ''}`}>Upload Data</Link>}
          <Link className="btn" to={`/sites?uploads=1${teamFilter !== 'all'?`&team=${encodeURIComponent(teamFilter)}`:''}`}>See uploads</Link>
          <Link className="btn primary" to="/?panel=add-site">
            Add site on the map
          </Link>
          {!adding && addTeam && (
            <button className="btn" type="button" onClick={() => setAdding(true)}>
              Add by coordinates
            </button>
          )}
        </div>
      </header>

      {error && <div className="banner err">{error}</div>}

      {params.get('uploads')==='1'?<UploadsList teams={teams} initialTeamId={teamFilter} onClose={()=>{const next=new URLSearchParams(params);next.delete('uploads');setParams(next);}}/>:uploading ? <SupplyChainImport embedded initialTeamId={teamFilter} initialImportId={params.get('import')} onComplete={(teamId)=>{
        setParams({team:teamId});
        load();
      }} onClose={()=>{const next=new URLSearchParams(params);next.delete('upload');next.delete('import');setParams(next);}} /> : <>

      {adding && addTeam && (
        <section>
          <h3>New site</h3>
          <SiteForm
            teams={teams}
            initial={{ teamId: addTeam.id }}
            onCancel={() => setAdding(false)}
            onSubmit={async (body) => {
              const site = await api(`/teams/${body.teamId ?? addTeam.id}/sites`, { method: 'POST', body });
              setAdding(false);
              navigate(`/sites/${site.id}`);
            }}
          />
        </section>
      )}

      <div className="controls site-filters">
        <input className="search" aria-label="Search sites" placeholder="Search name, address, place…" value={q} onChange={(e) => setParam('q', e.target.value)} />
        {teams.length > 1 && (
          <select className="filter-select" aria-label="Team" value={teamFilter} onChange={(e) => setParam('team', e.target.value)}>
            <option value="all">All teams</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
        <select className="filter-select" aria-label="Type" value={typeFilter} onChange={(e) => setParam('type', e.target.value)}>
          <option value="all">All types</option>
          {Object.entries(SITE_TYPES).map(([k, t]) => (
            <option key={k} value={k}>
              {t.label}
            </option>
          ))}
        </select>
        <select className="filter-select" aria-label="Status" value={statusFilter} onChange={(e) => setParam('status', e.target.value)}>
          <option value="all">Any status</option>
          {SITE_STATUSES.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </div>

      {!sites && !error && <div className="loading">Loading sites…</div>}
      {sites && shown.length === 0 && (
        <p className="muted">{sites.length ? 'No sites match these filters.' : 'No sites yet — upload a CSV, XLSX or JSON file, or add a site on the map.'}</p>
      )}
      {shown.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th className="left">Site</th>
                <th className="left">Area</th>
                <th className="left">Team</th>
                <th className="left">Monitoring</th>
                <th className="left">Active alerts</th>
                <th className="left">Routes</th>
                <th className="left" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map((s) => {
                const ts = typeStyle(s.type);
                const al = alertsBySite.get(s.id);
                const rs = routesBySite.get(s.id) ?? [];
                return (
                  <tr key={s.id}>
                    <td className="left">
                      <span className="site-dot" style={{ '--c': ts.color }} aria-hidden="true">
                        {ts.code}
                      </span>{' '}
                      <Link to={`/sites/${s.id}`}>{s.name}</Link>
                      <div className="muted small-note">
                        {s.typeLabel ?? ts.label} · {s.status} · {INDUSTRY_LABEL[s.industry] ?? s.industry}
                      </div>
                    </td>
                    <td className="left">{s.covered === false ? 'Outside US/Canada' : [s.municipality, s.countyName, s.state].filter(Boolean).join(', ')}</td>
                    <td className="left">{teams.find((t) => t.id === s.teamId)?.name ?? '—'}</td>
                    <td className="left">
                      {s.covered === false ? (
                        <span className="muted">Not monitored (no coverage)</span>
                      ) : s.status === 'active' || s.status === 'planned' ? (
                        'Alerts + weather'
                      ) : (
                        <span className="muted">Official alerts only ({s.status})</span>
                      )}
                    </td>
                    <td className="left">
                      {al ? <span className={`chip sev-${al.worst}`}>{`${al.count} · ${SEV_TEXT[al.worst] ?? ''}`}</span> : <span className="muted">None</span>}
                    </td>
                    <td className="left">
                      {rs.length ? rs.map((r) => (
                        <div key={r.id}>
                          <Link to={`/?route=${r.id}`}>{r.name}</Link>
                        </div>
                      )) : <span className="muted">—</span>}
                    </td>
                    <td className="left">
                      <Link className="btn" to={`/?site=${s.id}`}>
                        View on map
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>}
    </div>
  );
}
